import React, { useContext, useState, useEffect } from "react";
import useMediaQuery from '@mui/material/useMediaQuery';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import { Button, Paper, Alert } from '@mui/material';
import SubjectsChart from "../components/SubjectsChart";
import AttributesChart from "../components/AttributesChart";
import CommentsPanel from "../components/CommentsPanel";
import { ReviewsContext } from '../context/ReviewsContext';
import ImpactOverTimeChart from "../components/ImpactOverTimeChart";
import { Typography, useTheme, Container } from "@mui/material";
import DashboardIcon from '@mui/icons-material/Dashboard';
import InfoIcon from '@mui/icons-material/Info';
import RecommendIcon from '@mui/icons-material/Recommend';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DashboardOverview from "../components/DashboardOverview";
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const { view, reviewsData, timeframe, sortOption } = useContext(ReviewsContext);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  // Detect landscape orientation
  const isLandscape = useMediaQuery('(orientation: landscape)');
  const navigate = useNavigate();
  const [signupDate, setSignupDate] = useState(null);
  const formattedDate = signupDate ? signupDate.toLocaleDateString() : '';

  // Fetch user info
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/users/`, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        if (response.ok) {
          const userData = await response.json();
          // Store created_at date for display
          if (userData.created_at) {
            setSignupDate(new Date(userData.created_at));
          }
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };
    
    fetchUserInfo();
  }, []);

  const handleUnlockOngoingAnalysis = async () => {
    try {
      // Direct monthly checkout
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/payment-checkout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ plan: 'monthly' }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const { checkout_url } = await response.json();
      window.location.href = checkout_url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
    }
  };

  // Render page content (assuming active if rendered)
  return (
    <Box sx={{
      px: { xs: 2, lg: 4 },
      pt: { xs: 2, lg: 4 },
      pb: { xs: 2, lg: 4 },
      height: { xs: 'auto', lg: '100%' },
      minHeight: { xs: 'auto', lg: '100%' },
      boxSizing: 'border-box',
      overflow: { xs: 'visible', lg: 'auto' },
      background: 'linear-gradient(135deg, #f8f9fe 0%, #ffffff 100%)',
      opacity: 0,
      display: 'flex',
      flexDirection: 'column',
      animation: 'fadeIn 1s forwards',
      '@keyframes fadeIn': {
        from: { opacity: 0 },
        to: { opacity: 1 },
      }
    }}>
      <Container 
        maxWidth="xl" 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          height: { xs: 'auto', lg: '100%' },
          overflow: { xs: 'visible', lg: 'auto' },
          pb: { xs: 2, lg: 4 }
        }}
      >
        {/* Mobile Premium Banner removed - now only on Businesses page */}
        
        {/* Header Section - no flex grow, just takes its natural height */}
        <Box sx={{ 
          mb: { 
            xs: 4,
            lg: 6 
          },
          flexShrink: 0,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 3 },
          alignItems: { xs: 'flex-start', md: 'flex-start' },
          width: '100%',
          height: { xs: 'auto', md: '300px' }
        }}>
          {/* Title Section */}
          <Box sx={{ 
            flex: { xs: '1 1 100%', md: '1 1 33.33%' },
            px: { xs: 0, md: 2 },
            height: { xs: 'auto', md: '100%' }
          }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 800,
                fontSize: { 
                  xs: '1.75rem',
                  lg: '2.5rem'
                },
                color: '#0A2540',
                letterSpacing: '-0.02em',
                mb: { 
                  xs: 1,
                  lg: 2 
                },
                display: 'flex',
                alignItems: 'center',
                gap: { 
                  xs: 1,
                  lg: 2 
                }
              }}
            >
              <DashboardIcon sx={{ 
                fontSize: 'inherit',
                color: '#1976d2' 
              }} />
              Dashboard
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#546E7A',
                fontSize: { 
                  xs: '0.95rem',
                  lg: '1.1rem'
                },
              }}
            >
              Review analytics and insights for your business
            </Typography>
          </Box>
          
          {/* Quick Overview Section */}
          <Box sx={{ 
            flex: { xs: '1 1 100%', md: '1 1 33.33%' },
            px: { xs: 0, md: 2 },
            height: { xs: 'auto', md: '100%' }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography 
                variant="h6" 
                fontWeight="bold"
                sx={{ 
                  borderBottom: '2px solid #1976d2',
                  pb: 0.5,
                  display: 'inline-block',
                  fontSize: '1.25rem'
                }}
              >
                Quick Overview
              </Typography>
            </Box>
            
            {/* We're importing part of the DashboardOverview content directly here */}
            <Box 
              component="div" 
              id="quick-overview-content" 
              sx={{ 
                height: { xs: 'auto', md: 'calc(100% - 48px)' },  // Account for title and margins
                maxHeight: { md: 'calc(100% - 48px)' },
                overflowY: 'auto',
                overflowX: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}
            ></Box>
          </Box>
          
          {/* Recommended Actions Section */}
          <Box sx={{ 
            flex: { xs: '1 1 100%', md: '1 1 33.33%' },
            px: { xs: 0, md: 2 },
            height: { xs: 'auto', md: '100%' }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <RecommendIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography 
                variant="h6" 
                fontWeight="bold"
                sx={{ 
                  borderBottom: '2px solid #1976d2',
                  pb: 0.5,
                  display: 'inline-block',
                  fontSize: '1.25rem'
                }}
              >
                Recommended Actions
              </Typography>
            </Box>
            
            {/* We're importing part of the DashboardOverview content directly here */}
            <Box 
              component="div" 
              id="recommended-actions-content"
              sx={{ 
                height: { xs: 'auto', md: 'calc(100% - 40px)' },  // Account for title and margins
                maxHeight: { md: 'calc(100% - 40px)' },
                overflowY: 'auto',
                overflowX: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                mb: 1
              }}>
            </Box>
          </Box>
        </Box>
        
        {/* Hidden DashboardOverview for data handling */}
        <Box sx={{ display: 'none' }}>
          <DashboardOverview />
        </Box>
      
        {/* Grid Container */}
        <Box sx={{ 
          flex: { xs: 'none', lg: 1 },
          display: 'flex',
          minHeight: { xs: 'auto', lg: 0 },
          overflow: 'visible',
        }}>
          <Grid 
            container 
            spacing={2}
            sx={{ 
              width: '100%',
              height: { xs: 'auto', lg: '100%' },
              mb: 0,
              overflow: 'visible',
            }}
          >
            <Grid
              size={{ xs: 12, md: 3 }}
              sx={{
                height: { xs: '400px', lg: '100%' },
                overflow: 'visible',
              }}
            >
              <SubjectsChart />
            </Grid>
            <Grid
              size={{ xs: 12, md: 3 }}
              sx={{
                height: { xs: '400px', lg: '100%' },
                overflow: 'visible',
              }}
            >
              <AttributesChart />
            </Grid>
            <Grid
              size={{ xs: 12, md: 3 }}
              sx={{
                height: { xs: '400px', lg: '100%' },
                overflow: 'visible',
              }}
            >
              <ImpactOverTimeChart />
            </Grid>
            <Grid
              size={{ xs: 12, md: 3 }}
              sx={{
                height: { xs: '400px', lg: '100%' },
                overflow: 'visible',
              }}
            >
              <CommentsPanel />
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default Dashboard;
